import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    TextField, 
    Typography, 
    Paper, 
    Accordion, 
    AccordionSummary, 
    AccordionDetails,
    Divider
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { autoWebViewJs } from '@droid/android';
import { PermissionStatusPanel } from './components/PermissionStatusPanel';
import { PreAlarmConfig, EmergencyTTSSettings } from './components';
import toast from 'react-hot-toast';
import VoiceEmergencySettings, { VoiceEmergencySettingsRef } from './components/VoiceEmergencySettings';

interface FallDetectionSettingsProps {
    voiceSettingsRef?: React.RefObject<VoiceEmergencySettingsRef>;
}
// import { VoiceEmergencySettings } from '@src/components/voice-emergency-settings';

export const FallDetectionSettings: React.FC<FallDetectionSettingsProps> = ({ voiceSettingsRef }) => {
    const [threshold, setThreshold] = useState<string>('5.0');
    const [lastValidThreshold, setLastValidThreshold] = useState<string>('5.0');
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const localVoiceSettingsRef = useRef<VoiceEmergencySettingsRef>(null);
    const effectiveVoiceSettingsRef = voiceSettingsRef || localVoiceSettingsRef;
    
    // 权限面板展开状态和统计数据
    const [permissionExpanded, setPermissionExpanded] = useState(false);
    const [permissionStats, setPermissionStats] = useState({ granted: 0, denied: 0, unknown: 0 });

    // 从Android端获取阈值的函数
    const fetchThresholdFromAndroid = async (): Promise<string> => {
        try {
            const script = `
            var thresholdValue = com.fanfanlo.emergencycall.manager.SensorManager.getThreshold();
            thresholdValue;`;
            const { javaResultString } = autoWebViewJs.callScript(script);
            const thresholdValue = parseFloat(javaResultString || '5.0');
            return thresholdValue.toString();
        } catch (error) {
            console.error('Error fetching threshold value:', error);
            return '5.0';
        }
    };

    useEffect(() => {
        const initializeThreshold = async () => {
            const fetchedThreshold = await fetchThresholdFromAndroid();
            setThreshold(fetchedThreshold);
            setLastValidThreshold(fetchedThreshold);
        };
        initializeThreshold();

        // 清理函数
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);


    // 提交阈值到Android端
    const submitThreshold = async (thresholdValue: number): Promise<boolean> => {
        try {
            const script = `com.fanfanlo.emergencycall.manager.SensorManager.updateThreshold(${thresholdValue});`;
            autoWebViewJs.callScript(script);
            console.log(`阈值已设置为: ${thresholdValue}`);
            toast.success('阈值设置成功');
            return true;
        } catch (error) {
            console.error('Error calling updateThreshold:', error);
            toast.error('阈值设置失败:' + error);
            return false;
        }
    };

    // 验证阈值是否合法
    const isValidThreshold = (value: string): boolean => {
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue > 0;
    };

    // 显示toast提示
    const showToast = (message: string) => {
        // 使用简单的alert作为toast提示，实际项目中可以使用更好的toast库
        alert("showToast_fn" + message);
    };

    // 防抖的onChange处理函数
    const handleThresholdChange = (newValue: string) => {
        setThreshold(newValue);
        
        // 取消之前的定时器
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // 设置新的定时器
        debounceTimerRef.current = setTimeout(async () => {
            if (isValidThreshold(newValue)) {
                // 数值合法，提交更改
                const numValue = parseFloat(newValue);
                const success = await submitThreshold(numValue);
                if (success) {
                    setLastValidThreshold(newValue);
                } else {
                    showToast('设置阈值失败，请确保您在安卓应用中');
                    // 恢复为上次合法值
                    setThreshold(lastValidThreshold);
                }
            } else {
                // 数值不合法，提示并恢复
                showToast('请输入一个有效的数字作为阈值');
                if (lastValidThreshold) {
                    setThreshold(lastValidThreshold);
                } else {
                    // 如果没有存储的合法值，从Android端获取
                    const fetchedThreshold = await fetchThresholdFromAndroid();
                    setThreshold(fetchedThreshold);
                    setLastValidThreshold(fetchedThreshold);
                }
            }
        }, 1500); // 1.5秒延迟
    };




    return (
        <Box sx={{ p: 2 }}>
            {/* 传感器监控控制面板 */}
            <Paper elevation={1} sx={{ padding: 2, marginBottom: 2 }}>
                <Typography variant="h6" gutterBottom>
                    传感器
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                    <TextField
                        label="震动阈值"
                        variant="outlined"
                        type="number"
                        value={threshold}
                        onChange={(e) => handleThresholdChange(e.target.value)}
                        size="small"
                        helperText="摔倒、碰撞检测，数值越小越灵敏，一般在3-15之间，手机不同，需您自行测试。"
                    />
                </Box>
            </Paper>

            {/* 权限状态面板 */}
            <Accordion 
                sx={{ marginBottom: 2 }}
                expanded={permissionExpanded}
                onChange={(event, isExpanded) => setPermissionExpanded(isExpanded)}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="permission-panel-content"
                    id="permission-panel-header"
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            权限
                        </Typography>
                        
                        {/* 统计子标题 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        backgroundColor: 'success.main' 
                                    }} 
                                />
                                <Typography variant="caption" color="success.main">
                                    已授权/{permissionStats.granted}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        backgroundColor: 'error.main' 
                                    }} 
                                />
                                <Typography variant="caption" color="error.main">
                                    未授权/{permissionStats.denied}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box 
                                    sx={{ 
                                        width: 8, 
                                        height: 8, 
                                        borderRadius: '50%', 
                                        backgroundColor: 'warning.main' 
                                    }} 
                                />
                                <Typography variant="caption" color="warning.main">
                                    未知/{permissionStats.unknown}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <PermissionStatusPanel 
                        isExpanded={permissionExpanded}
                        onStatsChange={setPermissionStats}
                    />
                </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 3 }} />

            {/* 防误触报警设置 */}
            <Box sx={{ mb: 3 }}>
                {/* <Typography variant="h6" gutterBottom>
                    防误触设置
                </Typography> */}
                <PreAlarmConfig />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 紧急呼救语音设置 */}
            <Box sx={{ mb: 3 }}>
                <VoiceEmergencySettings ref={effectiveVoiceSettingsRef} />
            </Box>

        </Box>
    );
};

export default FallDetectionSettings;